import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Grid, Group, Text, Stack, Loader, Center,
  ThemeIcon, Paper, Badge, Table, Button, SegmentedControl
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconChartPie, IconTrendingUp, IconTrendingDown, IconDownload,
  IconTarget, IconCircleCheck, IconAlertTriangle, IconCircleX
} from '@tabler/icons-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import 'dayjs/locale/it';

const AREA_LABELS = {
  usave_fco: 'U-Save FCO', usave_cia: 'U-Save CIA',
  commercio_veicoli: 'Commercio Veicoli',
  consulenza_nlt: 'Consulenza NLT',
  generale: 'Generale'
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

function DashboardCdG() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meseDa, setMeseDa] = useState(() => {
    const fine = new Date();
    // Fine = mese precedente (escludi mese corrente)
    const fineNorm = new Date(fine.getFullYear(), fine.getMonth() - 1, 1);
    return new Date(fineNorm.getFullYear(), fineNorm.getMonth() - 11, 1);
  });
  const [meseA, setMeseA] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth() - 1, 1);
  });
  const [trendArea, setTrendArea] = useState('totale');

  useEffect(() => { loadData(); }, [meseDa, meseA]);

  const loadData = async () => {
    if (!meseDa || !meseA) return;
    setLoading(true);
    try {
      const da = `${meseDa.getFullYear()}-${String(meseDa.getMonth() + 1).padStart(2, '0')}-01`;
      const a = `${meseA.getFullYear()}-${String(meseA.getMonth() + 1).padStart(2, '0')}-01`;
      const res = await api.get('/cdg/dashboard', { params: { mese_da: da, mese_a: a } });
      setData(res.data);
    } catch {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare i dati CdG', color: 'red' });
    } finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const da = `${meseDa.getFullYear()}-${String(meseDa.getMonth() + 1).padStart(2, '0')}-01`;
      const a = `${meseA.getFullYear()}-${String(meseA.getMonth() + 1).padStart(2, '0')}-01`;
      const res = await api.get('/cdg/export-excel', { params: { mese_da: da, mese_a: a }, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CdG_report.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();
    } catch {
      notifications.show({ title: 'Errore', message: 'Errore export', color: 'red' });
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  if (loading && !data) return <Center h={400}><Loader size="lg" color="teal" /></Center>;
  if (!data) return null;

  const barData = data.aree
    .filter(a => a.area !== 'generale')
    .map(a => ({
      name: AREA_LABELS[a.area] || a.area,
      Ricavi: a.ricavi,
      Costi: a.costi_totali,
      Margine: a.margine
    }));

  const pieData = data.aree
    .filter(a => a.ricavi > 0)
    .map(a => ({ name: AREA_LABELS[a.area] || a.area, value: a.ricavi }));

  const kpiIcon = (stato) => {
    if (stato === 'verde') return <IconCircleCheck size={18} color="green" />;
    if (stato === 'giallo') return <IconAlertTriangle size={18} color="orange" />;
    return <IconCircleX size={18} color="red" />;
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconChartPie size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Controllo di Gestione</Title>
        </Group>
        <Group>
          <MonthPickerInput label="Da" locale="it" valueFormat="MMM YYYY" value={meseDa}
                            onChange={setMeseDa} style={{ width: 160 }} />
          <MonthPickerInput label="A" locale="it" valueFormat="MMM YYYY" value={meseA}
                            onChange={setMeseA} style={{ width: 160 }} />
          <Button variant="light" leftSection={<IconDownload size={18} />} onClick={handleExport} mt={24}>
            Export Excel
          </Button>
        </Group>
      </Group>

      {/* Summary Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Ricavi Totali</Text>
              <ThemeIcon color="green" variant="light" size="lg"><IconTrendingUp size={20} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{formatCurrency(data.riepilogo.totale_ricavi)}</Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Costi Totali</Text>
              <ThemeIcon color="red" variant="light" size="lg"><IconTrendingDown size={20} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="red">{formatCurrency(data.riepilogo.totale_costi)}</Text>
            <Group gap="xs" mt={4} wrap="wrap">
              <Text size="xs" c="dimmed">Pers: {formatCurrency(data.riepilogo.totale_costi_personale)}</Text>
              <Text size="xs" c="dimmed">Altri: {formatCurrency(data.riepilogo.totale_altri_costi)}</Text>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Margine</Text>
              <ThemeIcon color={data.riepilogo.margine >= 0 ? 'green' : 'red'} variant="light" size="lg">
                <IconTarget size={20} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={data.riepilogo.margine >= 0 ? 'green' : 'red'}>
              {formatCurrency(data.riepilogo.margine)}
            </Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Margine %</Text>
              <ThemeIcon color={data.riepilogo.margine_perc >= 0 ? 'green' : 'red'} variant="light" size="lg">
                <IconTarget size={20} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={data.riepilogo.margine_perc >= 0 ? 'green' : 'red'}>
              {data.riepilogo.margine_perc}%
            </Text>
          </Card>
        </Grid.Col>
      </Grid>

      {/* KPI Macro: 3 aree core */}
      {(() => {
        const getArea = (key) => data.aree.find(a => a.area === key) || { ricavi: 0, costi_totali: 0, margine: 0, margine_perc: 0 };
        const fco = getArea('usave_fco');
        const cia = getArea('usave_cia');
        const comp = getArea('commercio_veicoli');
        const nlt = getArea('consulenza_nlt');
        const usave = {
          ricavi: fco.ricavi + cia.ricavi,
          costi_totali: fco.costi_totali + cia.costi_totali,
          margine: fco.margine + cia.margine,
        };
        usave.margine_perc = usave.ricavi > 0 ? (usave.margine / usave.ricavi * 100).toFixed(1) : 0;
        const macroAree = [
          { label: 'Noleggio U-Save', subtitle: 'FCO + CIA', data: usave, color: '#0088FE' },
          { label: 'Compravendita Auto', subtitle: 'Commercio Veicoli', data: comp, color: '#00C49F' },
          { label: 'Consulenza NLT', subtitle: 'Noleggio Lungo Termine', data: nlt, color: '#FFBB28' },
        ];
        return (
          <Grid>
            {macroAree.map(({ label, subtitle, data: d, color }) => (
              <Grid.Col key={label} span={{ base: 12, md: 4 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Text fw={700} size="md">{label}</Text>
                  <Text size="xs" c="dimmed" mb="sm">{subtitle}</Text>
                  <Stack gap={4}>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Ricavi</Text>
                      <Text size="sm" fw={600} c="green">{formatCurrency(d.ricavi)}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Costi</Text>
                      <Text size="sm" fw={600} c="red">{formatCurrency(d.costi_totali)}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Margine</Text>
                      <Text size="sm" fw={700} c={d.margine >= 0 ? 'green' : 'red'}>{formatCurrency(d.margine)}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Margine %</Text>
                      <Badge color={d.margine_perc >= 10 ? 'green' : d.margine_perc >= 0 ? 'yellow' : 'red'} variant="light">
                        {d.margine_perc}%
                      </Badge>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        );
      })()}

      {/* Charts */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} mb="md">Ricavi vs Costi per Area</Text>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <ReTooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="Ricavi" fill="#00C49F" />
                <Bar dataKey="Costi" fill="#FF8042" />
                <Bar dataKey="Margine" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} mb="md">Distribuzione Ricavi</Text>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                     dataKey="value" labelLine={false}>
                  {pieData.map((_, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip formatter={(v) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Andamento mensile */}
      {data.serie_mensile && data.serie_mensile.length > 1 && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Andamento Mensile</Text>
            <SegmentedControl
              size="xs"
              value={trendArea}
              onChange={setTrendArea}
              data={[
                { value: 'totale', label: 'Totale' },
                { value: 'usave', label: 'U-Save' },
                { value: 'usave_fco', label: 'FCO' },
                { value: 'usave_cia', label: 'CIA' },
                { value: 'commercio_veicoli', label: 'Compravendita' },
                { value: 'consulenza_nlt', label: 'NLT' },
              ]}
            />
          </Group>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data.serie_mensile.map(m => {
              const label = new Date(m.mese + '-15').toLocaleDateString('it-IT', { month: 'short', year: '2-digit' });
              if (trendArea === 'totale') {
                return { mese: label, Ricavi: m.totale_ricavi, Costi: m.totale_costi, Margine: m.totale_margine };
              }
              if (trendArea === 'usave') {
                return {
                  mese: label,
                  Ricavi: (m.usave_fco_ricavi || 0) + (m.usave_cia_ricavi || 0),
                  Costi: (m.usave_fco_costi || 0) + (m.usave_cia_costi || 0),
                  Margine: (m.usave_fco_margine || 0) + (m.usave_cia_margine || 0),
                };
              }
              return {
                mese: label,
                Ricavi: m[`${trendArea}_ricavi`] || 0,
                Costi: m[`${trendArea}_costi`] || 0,
                Margine: m[`${trendArea}_margine`] || 0,
              };
            })}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mese" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <ReTooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Line type="monotone" dataKey="Ricavi" stroke="#00C49F" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Costi" stroke="#FF8042" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Margine" stroke="#0088FE" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Area Breakdown Table */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Text fw={600} mb="md">Dettaglio per Area</Text>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Area</Table.Th>
              <Table.Th ta="right">Ricavi</Table.Th>
              <Table.Th ta="right">Costi Personale</Table.Th>
              <Table.Th ta="right">Altri Costi</Table.Th>
              <Table.Th ta="right">Costi Totali</Table.Th>
              <Table.Th ta="right">Margine</Table.Th>
              <Table.Th ta="right">Margine %</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.aree.map(a => (
              <Table.Tr key={a.area}>
                <Table.Td><Text fw={600}>{AREA_LABELS[a.area] || a.area}</Text></Table.Td>
                <Table.Td ta="right">{formatCurrency(a.ricavi)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(a.costi_personale)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(a.altri_costi)}</Table.Td>
                <Table.Td ta="right">{formatCurrency(a.costi_totali)}</Table.Td>
                <Table.Td ta="right">
                  <Text fw={600} c={a.margine >= 0 ? 'green' : 'red'}>{formatCurrency(a.margine)}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Badge color={a.margine_perc >= 10 ? 'green' : a.margine_perc >= 0 ? 'yellow' : 'red'} variant="light">
                    {a.margine_perc}%
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr style={{ backgroundColor: '#f0f9ff' }}>
              <Table.Td><Text fw={700}>TOTALE</Text></Table.Td>
              <Table.Td ta="right"><Text fw={700}>{formatCurrency(data.riepilogo.totale_ricavi)}</Text></Table.Td>
              <Table.Td ta="right"><Text fw={700}>{formatCurrency(data.riepilogo.totale_costi_personale)}</Text></Table.Td>
              <Table.Td ta="right"><Text fw={700}>{formatCurrency(data.riepilogo.totale_altri_costi)}</Text></Table.Td>
              <Table.Td ta="right"><Text fw={700}>{formatCurrency(data.riepilogo.totale_costi)}</Text></Table.Td>
              <Table.Td ta="right">
                <Text fw={700} c={data.riepilogo.margine >= 0 ? 'green' : 'red'}>{formatCurrency(data.riepilogo.margine)}</Text>
              </Table.Td>
              <Table.Td ta="right">
                <Badge color={data.riepilogo.margine_perc >= 10 ? 'green' : data.riepilogo.margine_perc >= 0 ? 'yellow' : 'red'} variant="light" size="lg">
                  {data.riepilogo.margine_perc}%
                </Badge>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Card>

      {/* KPI */}
      {data.kpis && data.kpis.length > 0 && (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text fw={600} mb="md">KPI</Text>
          <Grid>
            {data.kpis.map(kpi => (
              <Grid.Col key={kpi.id} span={{ base: 12, sm: 6, md: 4 }}>
                <Paper p="md" withBorder radius="md">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">{AREA_LABELS[kpi.area] || kpi.area}</Text>
                    {kpiIcon(kpi.stato)}
                  </Group>
                  <Text fw={600} mt={4}>{kpi.nome}</Text>
                  <Group gap="xs" mt="xs">
                    <Text size="sm">Attuale: <Text span fw={700}>{kpi.attuale}{kpi.unita === '%' ? '%' : kpi.unita === '€' ? ' €' : ''}</Text></Text>
                    <Text size="sm" c="dimmed">Target: {kpi.target}{kpi.unita === '%' ? '%' : kpi.unita === '€' ? ' €' : ''}</Text>
                  </Group>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </Card>
      )}
    </Stack>
  );
}

export default DashboardCdG;
